'use client';

import type { FC } from 'react';
import { useSearchParams } from 'next/navigation';
import { Link } from '@packages/modules/src/Link/Link';
import { cn } from '@packages/shared/src/utils/cn/cn';

import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters/mergeQueryParameters';
import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { unmask } from '@packages/gql/src/betterMasking';
import { type FragmentType } from '@packages/gql/generated/shopping';
import { type CategoryMenuItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryMenuItemFragmentFragmentDoc';
import { type ProductCategoryNavigationDesktopFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductCategoryNavigationDesktopFragmentFragmentDoc';
import { extractDisplayMode, extractOrder, extractSelectedFilterValues } from '../../queryEncoding';
import { buildCategoryTree, type CategoryNode } from './buildCategoryTree';
import { ProductCategoryNavigationDesktopItemContent } from './ProductCategoryNavigationDesktop.ItemContent';

/* GraphQL */ `
  fragment CategoryItemTextFragment on FlatCategoryItem {
    name
    isSelected
  }
`;

/* GraphQL */ `
  fragment CategoryItemContentFragment on FlatCategoryItem {
    level
    pathName
    hasChildren
    isSelected
    ...CategoryItemTextFragment
  }
`;

/* GraphQL */ `
  fragment CategoryMenuItemFragment on FlatCategoryItem {
    id
    level
    parentId
    pathName
    isSelectedExact
    hasChildren
    isSelected
    ...CategoryItemContentFragment
  }
`;

type CategoryMenuItemProps = {
  maskedCategory: CategoryNode;
  selectedExactCategoryId: string | undefined;
};

/** Displays MenuItem for every nested child through recursion */
const CategoryMenuItem: FC<CategoryMenuItemProps> = ({
  maskedCategory,
  selectedExactCategoryId,
}) => {
  const searchParams = useSearchParams();
  const query = searchParamsToObject(searchParams);
  const category = unmask<typeof CategoryMenuItemFragmentFragmentDoc>(maskedCategory);
  const { children: subcategories } = maskedCategory;

  const isChildOfActiveCategory = category.parentId === selectedExactCategoryId;

  const href = isChildOfActiveCategory
    ? mergeQueryParameters(category.pathName, {
        ...extractDisplayMode(query),
        ...extractOrder(query),
        ...extractSelectedFilterValues(query),
      })
    : category.pathName;

  const categoryLevelPaddingLeftMap: Record<number, string> = {
    2: 'pl-1',
    3: 'pl-1',
    4: 'pl-2',
    5: 'pl-3',
  };
  return (
    <li>
      {/** using old MUI Link untill the Link from Module/src gets fixed for page transitions */}
      <Link
        aria-expanded={category.hasChildren ? category.isSelected || false : undefined}
        aria-current={category.isSelectedExact ? 'page' : undefined}
        className={`hover:bg-color-grey-light text-color-accordion-toggle-icon flex min-h-4 flex-1 items-center py-0.5 pr-2 no-underline ${categoryLevelPaddingLeftMap[category.level] || ''}`}
        href={href}
      >
        <div
          className={cn(
            'flex flex-1 items-center justify-between',
            category.level > 2 ? 'text-size-body-sm' : 'text-size-body-base',
          )}
        >
          <ProductCategoryNavigationDesktopItemContent maskedCategory={category} />
        </div>
      </Link>
      {subcategories.length > 0 && (
        <ul className="list-none">
          {subcategories.map((item) => (
            <CategoryMenuItem
              key={item.id}
              selectedExactCategoryId={selectedExactCategoryId}
              maskedCategory={item}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/* GraphQL */ `
  fragment ProductCategoryNavigationDesktopFragment on FlatCategoryItem {
    id
    name
    parentId
    isSelectedExact
    # This property is not used in this component, but only as a workaround
    isSelectedIndirectly
    isSelected
    level
    pathName
    hasChildren
    ...CategoryMenuItemFragment
  }
`;

export type ProductCategoryNavigationDesktopProps = {
  maskedItems: Array<FragmentType<typeof ProductCategoryNavigationDesktopFragmentFragmentDoc>>;
};

/**
 * Displays the category navigation in desktop view
 * */
export const ProductCategoryNavigationDesktop: FC<ProductCategoryNavigationDesktopProps> = ({
  maskedItems,
}) => {
  const items = unmask<typeof ProductCategoryNavigationDesktopFragmentFragmentDoc>(
    maskedItems,
  ).filter((item) => item.level >= 2); // forward compatibility for future changes, where `root` (really the L1 category) will be included in the categories itself and not provided separately

  const selectedExactCategory = items.find((item) => item.isSelectedExact);
  const categoryItems = buildCategoryTree(items);
  return (
    <ul className="list-none pt-1" data-testid="product-category-navigation-desktop">
      {categoryItems.map((item) => (
        <CategoryMenuItem
          key={item.id}
          maskedCategory={item}
          selectedExactCategoryId={selectedExactCategory?.id}
        />
      ))}
    </ul>
  );
};
